import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Tabs,
  Tab
} from '@material-ui/core'
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@material-ui/icons'
import { Alert } from '@material-ui/lab'
import axios from 'axios'

const CalculationConfigEditor = ({ configId, onSave, onClose }) => {
  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState(0)

  useEffect(() => {
    if (configId) {
      loadConfig()
    } else {
      // Create new config with default values
      setConfig({
        name: '',
        description: '',
        isActive: true,
        plantRates: {
          flatFlowers: 80,
          pintFlowers: 80,
          quartFlowers: 80,
          smallPlants: 80,
          mediumPlants: 60,
          largePlants: 15,
          mediumTrees: 15,
          largeTrees: 15,
          extraLargeTrees: 4,
          largePlants24: 15,
          smallTrees36: 4,
          mediumTrees48: 3,
          mediumTrees54: 2,
          largeTrees60: 2,
          largeTrees66: 2,
          largeTrees72: 1,
          largeTrees84: 1
        },
        equipment: {
          bobcatDailyRate: 1200,
          craneDailyRate: 3600,
          tractorRentalDailyRate: 800,
          tractorInhouseDailyRate: 400,
          bobcatRentalDailyRate: 600,
          bobcatInhouseDailyRate: 300
        },
        labor: {
          dayRate: 2240
        },
        materialRates: {
          gravelTonsPerDay: 20,
          boulderTonsPerDay: 15
        },
        gravelCoverage: {
          quarterInch: 160,
          halfInch: 120,
          threeQuarterInch: 110,
          oneInch: 90,
          ripRap: 50
        },
        moveGravelRates: {
          gravelTonsPerDayHand: 3,
          gravelTonsPerDayTractor: 40,
          gravelTonsPerDayBobcat: 35
        },
        difficultyFactors: {
          veryEasy: 0.5,
          standard: 1.0,
          slightlyDifficult: 1.25,
          difficult: 1.5,
          veryDifficult: 1.75,
          extremelyDifficult: 2.0
        },
        soilFactors: {
          sandy: 0.9,
          loamy: 1.0,
          clay: 1.3,
          rocky: 1.5
        },
        seasonalFactors: {
          spring: 1.0,
          summer: 1.1,
          fall: 1.0,
          winter: 1.2
        },
        irrigation: {
          plantsPerValve: 120,
          treesPerValve: 12,
          daysPerValve: 1.5,
          costPerValve: 1250,
          clockCost: 1250,
          backflowCost: 1000
        },
        lighting: {
          wattsPerLight: 10,
          costPerLight: 160,
          techDailyRate: 1200,
          lightsInstalledPerDay: 20,
          hoursTrenchingPerLight: 1,
          wiringAndConnectorCostPerLight: 15,
          hourlyTrenchingRate: 70,
          transformerCosts: {
            w150: 225,
            w300: 450,
            w600: 900,
            w900: 1350,
            w1200: 1800,
            custom: 9999
          }
        },
        boulders: {
          costPerTon: 150
        },
        gravel: {
          costPerTonOver9: 75,
          costPerTonUnder9: 100
        }
      })
      setLoading(false)
    }
  }, [configId])

  const loadConfig = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`/api/calculation-configs/${configId}`)
      setConfig(response.data.config)
      setError(null)
    } catch (err) {
      console.error('Error loading config:', err)
      setError('Failed to load configuration')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      if (configId) {
        await axios.put(`/api/calculation-configs/${configId}`, config)
      } else {
        await axios.post('/api/calculation-configs', config)
      }

      if (onSave) onSave()
    } catch (err) {
      console.error('Error saving config:', err)
      setError(err.response?.data?.error || 'Failed to save configuration')
    } finally {
      setSaving(false)
    }
  }

  const updateField = (section, field, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: parseFloat(value) || 0
      }
    }))
  }

  const updateBasicField = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }))
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (!config) return null

  return (
    <Box style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingBottom: 40 }}>
      <Container maxWidth="lg" style={{ paddingTop: 32 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          style={{
            marginBottom: 32,
            padding: '24px 32px',
            backgroundColor: '#fff',
            borderRadius: 8,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Typography variant="h4" component="h1" style={{ fontWeight: 500 }}>
            {configId ? 'Edit Configuration' : 'New Configuration'}
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              style={{ marginRight: 16 }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            {onClose && (
              <Button variant="outlined" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" style={{ marginBottom: 24 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Basic Information */}
        <Card style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent style={{ padding: 32 }}>
            <Typography variant="h6" gutterBottom style={{ marginBottom: 24, fontWeight: 500 }}>
              Basic Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Name"
                  value={config.name}
                  onChange={(e) => updateBasicField('name', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" style={{ height: '100%' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.isActive}
                        onChange={(e) => updateBasicField('isActive', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={config.description}
                  onChange={(e) => updateBasicField('description', e.target.value)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabbed Sections */}
        <Card style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            style={{ borderBottom: '1px solid rgba(0,0,0,0.12)' }}
          >
            <Tab label="Plant Rates" />
            <Tab label="Equipment & Labor" />
            <Tab label="Materials" />
            <Tab label="Factors" />
            <Tab label="Irrigation & Lighting" />
          </Tabs>
          <CardContent style={{ padding: 32 }}>

            {/* Tab 0: Plant Rates */}
            {activeTab === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom style={{ marginBottom: 16, fontWeight: 500 }}>
                  Plant Installation Rates (Plants per Day)
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  How many plants of each size can a crew install per day
                </Typography>
                <Grid container spacing={3}>
                  {Object.entries(config.plantRates).map(([key, value]) => (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <TextField
                  variant="outlined"
                        fullWidth
                        type="number"
                        label={formatLabel(key)}
                        value={value}
                        onChange={(e) => updateField('plantRates', key, e.target.value)}
                        InputProps={{ inputProps: { min: 0, step: 1 } }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Tab 1: Equipment & Labor */}
            {activeTab === 1 && (
              <>
                <Card style={{ marginBottom: 24 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom style={{ marginBottom: 16, fontWeight: 500 }}>
                      Equipment Daily Rates
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Daily rental costs for equipment
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          variant="outlined"
                          fullWidth
                          type="number"
                          label="Bobcat Daily Rate"
                          value={config.equipment.bobcatDailyRate}
                          onChange={(e) => updateField('equipment', 'bobcatDailyRate', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          variant="outlined"
                          fullWidth
                          type="number"
                          label="Crane Daily Rate"
                          value={config.equipment.craneDailyRate}
                          onChange={(e) => updateField('equipment', 'craneDailyRate', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          variant="outlined"
                          fullWidth
                          type="number"
                          label="Tractor Rental Daily Rate"
                          value={config.equipment.tractorRentalDailyRate}
                          onChange={(e) => updateField('equipment', 'tractorRentalDailyRate', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          variant="outlined"
                          fullWidth
                          type="number"
                          label="Tractor In-House Daily Rate"
                          value={config.equipment.tractorInhouseDailyRate}
                          onChange={(e) => updateField('equipment', 'tractorInhouseDailyRate', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          variant="outlined"
                          fullWidth
                          type="number"
                          label="Bobcat Rental Daily Rate"
                          value={config.equipment.bobcatRentalDailyRate}
                          onChange={(e) => updateField('equipment', 'bobcatRentalDailyRate', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          variant="outlined"
                          fullWidth
                          type="number"
                          label="Bobcat In-House Daily Rate"
                          value={config.equipment.bobcatInhouseDailyRate}
                          onChange={(e) => updateField('equipment', 'bobcatInhouseDailyRate', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom style={{ fontWeight: 500 }}>
                      Labor Rates
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          variant="outlined"
                          fullWidth
                          type="number"
                          label="Day Rate (per crew)"
                          value={config.labor.dayRate}
                          onChange={(e) => updateField('labor', 'dayRate', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Tab 2: Materials */}
            {activeTab === 2 && (
              <>
                <Card style={{ marginBottom: 24 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Material Installation Rates
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Gravel Tons Per Day (by hand)"
                          value={config.materialRates.gravelTonsPerDay}
                          onChange={(e) => updateField('materialRates', 'gravelTonsPerDay', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Boulder Tons Per Day"
                          value={config.materialRates.boulderTonsPerDay}
                          onChange={(e) => updateField('materialRates', 'boulderTonsPerDay', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Card style={{ marginBottom: 24 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Gravel Coverage (Sq Ft per Ton at 2" Depth)
                    </Typography>
                    <Grid container spacing={2}>
                      {Object.entries(config.gravelCoverage).map(([key, value]) => (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                          <TextField
                        variant="outlined"
                            fullWidth
                            type="number"
                            label={formatLabel(key)}
                            value={value}
                            onChange={(e) => updateField('gravelCoverage', key, e.target.value)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>

                <Card style={{ marginBottom: 24 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Gravel Moving Rates (Tons per Day)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="By Hand (per person)"
                          value={config.moveGravelRates.gravelTonsPerDayHand}
                          onChange={(e) => updateField('moveGravelRates', 'gravelTonsPerDayHand', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="With Tractor"
                          value={config.moveGravelRates.gravelTonsPerDayTractor}
                          onChange={(e) => updateField('moveGravelRates', 'gravelTonsPerDayTractor', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="With Bobcat"
                          value={config.moveGravelRates.gravelTonsPerDayBobcat}
                          onChange={(e) => updateField('moveGravelRates', 'gravelTonsPerDayBobcat', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Card style={{ marginBottom: 24 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Material Costs
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Boulder Cost Per Ton"
                          value={config.boulders.costPerTon}
                          onChange={(e) => updateField('boulders', 'costPerTon', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Gravel Cost Per Ton (10+ tons)"
                          value={config.gravel.costPerTonOver9}
                          onChange={(e) => updateField('gravel', 'costPerTonOver9', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Gravel Cost Per Ton (<10 tons)"
                          value={config.gravel.costPerTonUnder9}
                          onChange={(e) => updateField('gravel', 'costPerTonUnder9', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Tab 3: Factors */}
            {activeTab === 3 && (
              <>
                <Card style={{ marginBottom: 24 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Difficulty Factors (Labor Multipliers)
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Multipliers for adjusting labor based on site difficulty
                    </Typography>
                    <Grid container spacing={2}>
                      {Object.entries(config.difficultyFactors).map(([key, value]) => (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                          <TextField
                        variant="outlined"
                            fullWidth
                            type="number"
                            label={formatLabel(key)}
                            value={value}
                            onChange={(e) => updateField('difficultyFactors', key, e.target.value)}
                            InputProps={{ inputProps: { min: 0, step: 0.1 } }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>

                <Card style={{ marginBottom: 24 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Soil Factors (Labor Multipliers)
                    </Typography>
                    <Grid container spacing={2}>
                      {Object.entries(config.soilFactors).map(([key, value]) => (
                        <Grid item xs={12} sm={6} md={3} key={key}>
                          <TextField
                        variant="outlined"
                            fullWidth
                            type="number"
                            label={formatLabel(key)}
                            value={value}
                            onChange={(e) => updateField('soilFactors', key, e.target.value)}
                            InputProps={{ inputProps: { min: 0, step: 0.1 } }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Seasonal Factors (Labor Multipliers)
                    </Typography>
                    <Grid container spacing={2}>
                      {Object.entries(config.seasonalFactors).map(([key, value]) => (
                        <Grid item xs={12} sm={6} md={3} key={key}>
                          <TextField
                        variant="outlined"
                            fullWidth
                            type="number"
                            label={formatLabel(key)}
                            value={value}
                            onChange={(e) => updateField('seasonalFactors', key, e.target.value)}
                            InputProps={{ inputProps: { min: 0, step: 0.1 } }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Tab 4: Irrigation & Lighting */}
            {activeTab === 4 && (
              <>
                <Card style={{ marginBottom: 24 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Irrigation Constants
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Plants Per Valve"
                          value={config.irrigation.plantsPerValve}
                          onChange={(e) => updateField('irrigation', 'plantsPerValve', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Trees Per Valve"
                          value={config.irrigation.treesPerValve}
                          onChange={(e) => updateField('irrigation', 'treesPerValve', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Days Per Valve"
                          value={config.irrigation.daysPerValve}
                          onChange={(e) => updateField('irrigation', 'daysPerValve', e.target.value)}
                          InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Cost Per Valve"
                          value={config.irrigation.costPerValve}
                          onChange={(e) => updateField('irrigation', 'costPerValve', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Clock Cost"
                          value={config.irrigation.clockCost}
                          onChange={(e) => updateField('irrigation', 'clockCost', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Backflow Cost"
                          value={config.irrigation.backflowCost}
                          onChange={(e) => updateField('irrigation', 'backflowCost', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Lighting Constants
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Watts Per Light"
                          value={config.lighting.wattsPerLight}
                          onChange={(e) => updateField('lighting', 'wattsPerLight', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Cost Per Light"
                          value={config.lighting.costPerLight}
                          onChange={(e) => updateField('lighting', 'costPerLight', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Tech Daily Rate"
                          value={config.lighting.techDailyRate}
                          onChange={(e) => updateField('lighting', 'techDailyRate', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Lights Installed Per Day"
                          value={config.lighting.lightsInstalledPerDay}
                          onChange={(e) => updateField('lighting', 'lightsInstalledPerDay', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Hours Trenching Per Light"
                          value={config.lighting.hoursTrenchingPerLight}
                          onChange={(e) => updateField('lighting', 'hoursTrenchingPerLight', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Wiring & Connector Cost Per Light"
                          value={config.lighting.wiringAndConnectorCostPerLight}
                          onChange={(e) => updateField('lighting', 'wiringAndConnectorCostPerLight', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Hourly Trenching Rate"
                          value={config.lighting.hourlyTrenchingRate}
                          onChange={(e) => updateField('lighting', 'hourlyTrenchingRate', e.target.value)}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                    </Grid>

                    <Divider style={{ margin: "24px 0" }} />

                    <Typography variant="subtitle1" gutterBottom>
                      Transformer Costs
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="150W (0-15 lights)"
                          value={config.lighting.transformerCosts.w150}
                          onChange={(e) => updateField('lighting', 'transformerCosts', { ...config.lighting.transformerCosts, w150: parseFloat(e.target.value) || 0 })}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="300W (16-30 lights)"
                          value={config.lighting.transformerCosts.w300}
                          onChange={(e) => updateField('lighting', 'transformerCosts', { ...config.lighting.transformerCosts, w300: parseFloat(e.target.value) || 0 })}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="600W (31-60 lights)"
                          value={config.lighting.transformerCosts.w600}
                          onChange={(e) => updateField('lighting', 'transformerCosts', { ...config.lighting.transformerCosts, w600: parseFloat(e.target.value) || 0 })}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="900W (61-90 lights)"
                          value={config.lighting.transformerCosts.w900}
                          onChange={(e) => updateField('lighting', 'transformerCosts', { ...config.lighting.transformerCosts, w900: parseFloat(e.target.value) || 0 })}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="1200W (91-120 lights)"
                          value={config.lighting.transformerCosts.w1200}
                          onChange={(e) => updateField('lighting', 'transformerCosts', { ...config.lighting.transformerCosts, w1200: parseFloat(e.target.value) || 0 })}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                        variant="outlined"
                          fullWidth
                          type="number"
                          label="Custom (121+ lights)"
                          value={config.lighting.transformerCosts.custom}
                          onChange={(e) => updateField('lighting', 'transformerCosts', { ...config.lighting.transformerCosts, custom: parseFloat(e.target.value) || 0 })}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Save Button at Bottom */}
            <Box style={{ marginTop: 32, display: 'flex', justifyContent: 'flex-end' }}>
              {onClose && (
                <Button variant="outlined" onClick={onClose} disabled={saving} style={{ marginRight: 16 }}>
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  )
}

// Helper function to format field labels
const formatLabel = (key) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .replace(/Per/g, 'per')
    .replace(/And/g, 'and')
}

export default CalculationConfigEditor
